import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trackArticleView, trackReadingTime, trackScrollDepth, trackSocialShare } from '../utils/analytics';
import { resolveApiBaseUrl } from '../utils/apiBase';
import { formatDateTimeLong } from '../utils/formatDate';
import { fetchArticleEntities, fetchRelatedArticles, buildWebEntityUrl, buildWebFromArticleUrl } from '../services/graphApi';
import { usePageMeta, SITE_URL, DEFAULT_OG_IMAGE } from '../hooks/usePageMeta';
import { PageLoading, PageError } from '../components/PageStatus';
import { stripHtml, truncateText } from '../utils/text';
import { articleCategoryLabels, formatArticleCategories, displayCategoryLabel } from '../utils/articleCategories';
import styles from './ArticlePage.module.css';

const API_BASE = resolveApiBaseUrl();

function RelatedArticlesSection({
  relatedLoading,
  relatedArticles,
  prominent = false,
}) {
  if (!relatedLoading && relatedArticles.length === 0) {
    return null;
  }

  const className = [
    styles['related-articles'],
    prominent ? styles['related-articles--prominent'] : '',
  ].filter(Boolean).join(' ');

  return (
    <section className={className} aria-label="Related articles">
      <h3>Related articles</h3>
      {relatedLoading && (
        <p className={styles['article-sidebar-status']}>Finding related stories…</p>
      )}
      {!relatedLoading && relatedArticles.length > 0 && (
        <ul className={styles['related-articles-list']}>
          {relatedArticles.map((related) => {
            const relatedCats = formatArticleCategories(related);
            return (
              <li key={related.id}>
                <Link to={`/article/${related.id}`} className={styles['related-articles-link']}>
                  {related.title}
                </Link>
                {(related.source || relatedCats) && (
                  <span className={styles['related-articles-meta']}>
                    {[related.source, relatedCats].filter(Boolean).join(' · ')}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [graphEntities, setGraphEntities] = useState([]);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [scrollDepth, setScrollDepth] = useState(0);
  const articleRef = useRef(null);

  const pageDescription = useMemo(() => {
    if (!article) return undefined;
    const raw = article.summary ? stripHtml(article.summary) : article.title;
    return truncateText(raw);
  }, [article]);

  const articleJsonLd = useMemo(() => {
    if (!article) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: article.title,
      datePublished: article.publishedAt || undefined,
      author: article.author
        ? { '@type': 'Person', name: article.author }
        : { '@type': 'Organization', name: article.source || 'DarkNews' },
      publisher: {
        '@type': 'Organization',
        name: 'DarkNews',
        url: SITE_URL,
      },
      mainEntityOfPage: `${SITE_URL}/article/${article.id}`,
      image: article.imageUrl || DEFAULT_OG_IMAGE,
      description: pageDescription,
    };
  }, [article, pageDescription]);

  usePageMeta({
    title: article?.title || 'Article',
    description: pageDescription,
    path: id ? `/article/${id}` : undefined,
    image: article?.imageUrl || DEFAULT_OG_IMAGE,
    type: 'article',
    jsonLd: articleJsonLd,
  });

  const loadArticle = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      setStartTime(Date.now());

      const response = await fetch(`${API_BASE}/feed/${id}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Article not found');
        }
        throw new Error(`Failed to fetch article: ${response.status} ${response.statusText}`);
      }

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        throw new Error('Invalid JSON response from server');
      }
      setArticle(data);
      trackArticleView(id, data.title, data.source);
    } catch (err) {
      setError(err.message);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      return undefined;
    }
    setEntitiesLoading(true);
    fetchArticleEntities(id, { limit: 8 })
      .then((entities) => {
        if (!cancelled) {
          setGraphEntities(entities);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGraphEntities([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setEntitiesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      return undefined;
    }
    setRelatedLoading(true);
    fetchRelatedArticles(id, { limit: 6 })
      .then((articles) => {
        if (!cancelled) {
          setRelatedArticles(articles);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRelatedArticles([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setRelatedLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Track reading time when user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (startTime && article) {
        const timeSpent = Math.round((Date.now() - startTime) / 1000);
        trackReadingTime(article.id, timeSpent);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [startTime, article]);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      if (articleRef.current) {
        const element = articleRef.current;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;
        const depth = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
        
        if (depth > scrollDepth && depth % 25 === 0) { // Track at 25%, 50%, 75%, 100%
          setScrollDepth(depth);
          trackScrollDepth(article.id, depth);
        }
      }
    };

    const element = articleRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, [article, scrollDepth]);

  const formatDate = (dateString) =>
    dateString ? formatDateTimeLong(dateString) : 'Unknown date';

  const calculateReadingTime = (content) => {
    if (!content) return 0;
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const handleViewOriginal = () => {
    if (article?.url) {
      window.location.href = article.url;
    }
  };

  const handleSocialShare = (platform) => {
    trackSocialShare(platform, article.id);
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const shareTitle = article?.title || 'Article on DarkNews';
    const shareText = article?.summary || shareTitle;

    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareTitle} ${url}`)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className={styles['article-page']}>
        <div className="container">
          <PageLoading message="Loading article…" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['article-page']}>
        <div className="container">
          <PageError
            title="Article not found"
            message={error}
            onRetry={loadArticle}
          >
            <Link to="/feed" className="btn btn-primary mt-2">
              Back to Feed
            </Link>
          </PageError>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={styles['article-page']}>
        <div className="container">
          <PageError title="Article not found" message="The requested article could not be found.">
            <Link to="/feed" className="btn btn-primary mt-2">
              Back to Feed
            </Link>
          </PageError>
        </div>
      </div>
    );
  }

  const readingTime = calculateReadingTime(article.content);
  /* Multi-seed Starburst — only when this article has graph entities. */
  const primaryWebUrl =
    article.id && graphEntities.length > 0
      ? buildWebFromArticleUrl(article.id, { layout: 'starburst' })
      : null;
  const hasSummary = Boolean(article.summary?.trim());

  return (
    <div className={styles['article-page']}>
      <div className="container">
        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb" className={styles['article-breadcrumb']}>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/feed">Feed</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {article.title}
            </li>
          </ol>
        </nav>

        {/* Article Header */}
        <div className={styles['article-layout']}>
          <div className={styles['article-main']} ref={hasSummary ? undefined : articleRef}>
        <div className={`${styles['article-header']}${hasSummary ? '' : ` ${styles['article-header--compact']}`}`}>
          <h1 className={styles['article-title']}>{article.title}</h1>
          
          <div className={styles['article-meta']}>
            <div className="meta-item">
              <strong>Source:</strong> {article.source}
            </div>
            {article.author && (
              <div className="meta-item">
                <strong>Author:</strong> {article.author}
              </div>
            )}
            <div className="meta-item">
              <strong>Published:</strong> {formatDate(article.publishedAt)}
            </div>
            {readingTime > 0 && (
              <div className="meta-item">
                <strong>Reading Time:</strong> {readingTime} min read
              </div>
            )}
            {article.wordCount && (
              <div className="meta-item">
                <strong>Word Count:</strong> {article.wordCount.toLocaleString()}
              </div>
            )}
            {articleCategoryLabels(article).length > 0 && (
              <div className={`meta-item ${styles['article-categories-meta']}`} title="Story categories">
                <strong>Topics:</strong>{' '}
                {articleCategoryLabels(article).map((label) => (
                  <span key={label} className={`badge ${styles['category-badge']} me-1`}>
                    {displayCategoryLabel(label)}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles['article-actions']}>
            <button 
              onClick={handleViewOriginal}
              className="btn btn-outline-primary me-2"
            >
              View Original
            </button>
            
            <div className="btn-group" role="group">
              <button 
                onClick={() => {
                  handleSocialShare('twitter');
                  handleShare('twitter');
                }}
                className="btn btn-outline-secondary"
                title="Share on Twitter"
              >
                <i className="bi bi-twitter"></i>
              </button>
              <button 
                onClick={() => {
                  handleSocialShare('facebook');
                  handleShare('facebook');
                }}
                className="btn btn-outline-secondary"
                title="Share on Facebook"
              >
                <i className="bi bi-facebook"></i>
              </button>
              <button 
                onClick={() => {
                  handleSocialShare('linkedin');
                  handleShare('linkedin');
                }}
                className="btn btn-outline-secondary"
                title="Share on LinkedIn"
              >
                <i className="bi bi-linkedin"></i>
              </button>
            </div>
          </div>
        </div>

        {!hasSummary && (
          <RelatedArticlesSection
            relatedLoading={relatedLoading}
            relatedArticles={relatedArticles}
            prominent
          />
        )}

        {/* Article Content */}
        {hasSummary && (
        <div className={styles['article-content']} ref={articleRef}>
          <div className={styles['article-summary']}>
            <h3>Summary</h3>
            <div 
              className={styles['summary-content']}
              dangerouslySetInnerHTML={{ __html: article.summary }}
            />
          </div>

          {/* Temporarily commented out due to potential content size issues
          {article.content && (
            <div className="article-body">
              <h3>Full Article</h3>
              <div 
                className="content-body"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          )}
          */}
        </div>
        )}

        {hasSummary && (
          <RelatedArticlesSection
            relatedLoading={relatedLoading}
            relatedArticles={relatedArticles}
          />
        )}

        {/* Article Footer */}
        <div className={styles['article-footer']}>
          {(articleCategoryLabels(article).length > 0 || (article.tags && article.tags.length > 0)) && (
            <div className={styles['article-tags']}>
              {articleCategoryLabels(article).map((label) => (
                <span key={label} className={`badge ${styles['category-badge']} me-2`}>
                  {displayCategoryLabel(label)}
                </span>
              ))}
              {article.tags && article.tags.map((tag, index) => (
                <span key={`tag-${index}`} className="badge bg-secondary me-2">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className={styles['article-source-link']}>
            <small>
              Original article from: <a href={article.url} target="_blank" rel="noopener noreferrer">
                {article.sourceUrl || article.url}
              </a>
            </small>
          </div>
        </div>
          </div>

          <aside className={styles['article-sidebar']} aria-label="Story graph entities">
            <div className={styles['article-sidebar-header']}>
              <h2 className={styles['article-sidebar-title']}>Story graph</h2>
              {primaryWebUrl && (
                <Link to={primaryWebUrl} className={styles['article-sidebar-web-link']}>
                  Web →
                </Link>
              )}
            </div>
            {entitiesLoading && (
              <p className={styles['article-sidebar-status']}>Loading entities…</p>
            )}
            {!entitiesLoading && graphEntities.length === 0 && (
              <p className={styles['article-sidebar-status']}>
                No graph entities linked to this article yet.
              </p>
            )}
            {!entitiesLoading && graphEntities.length > 0 && (
              <ul className={styles['article-entity-list']}>
                {graphEntities.map((entity) => (
                  <li key={entity.id}>
                    <Link
                      to={buildWebEntityUrl(entity)}
                      className={styles['article-entity-chip']}
                    >
                      <span className={styles['article-entity-label']}>{entity.label}</span>
                      <span className={styles['article-entity-type']}>{entity.type}</span>
                    </Link>
                    {entity.slug && (
                      <Link
                        to={`/entity/${entity.slug}`}
                        className={styles['article-entity-timeline']}
                      >
                        Timeline
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;
